/**
 * Resolve a product/material/task-item thumbnail URL from common image shapes
 * (array of { url, filename }, single object, JSON string, or plain URL/filename).
 *
 * Matches Settings > Products display rules: prefer absolute http(s) urls, otherwise
 * treat url/filename as a storage path under imageBaseUrl (same as product.vue).
 */

function isAbsoluteHttpUrl (value) {
  if (value == null) return false
  const s = String(value).trim()
  if (!s) return false
  if (/^https?:\/\//i.test(s)) return true
  // Protocol-relative CDN urls (e.g. Reece enrichment before https: prefix)
  if (s.startsWith('//') && s.length > 2) return true
  return false
}

function absolutizeUrl (value) {
  const s = String(value).trim()
  if (s.startsWith('//')) return 'https:' + s
  return s
}

/** Bundled assets / data URIs that must not be prefixed with image storage base. */
function isDirectAssetUrl (value) {
  if (value == null) return false
  const s = String(value).trim()
  if (!s) return false
  return s.startsWith('/') || s.startsWith('data:') || s.startsWith('blob:')
}

function resolveImage (image, base) {
  if (!image) return null
  if (Array.isArray(image)) {
    if (image.length === 0) return null
    return resolveImage(image[0], base)
  }
  if (typeof image === 'string') {
    const trimmed = image.trim()
    if (!trimmed) return null
    if (
      (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
      (trimmed.startsWith('{') && trimmed.endsWith('}'))
    ) {
      try {
        return resolveImage(JSON.parse(trimmed), base)
      } catch (e) { /* ignore */ }
    }
    if (isAbsoluteHttpUrl(trimmed)) return absolutizeUrl(trimmed)
    if (isDirectAssetUrl(trimmed)) return trimmed
    return base + trimmed
  }
  if (typeof image !== 'object') return null

  const rawUrl = image.url != null ? String(image.url).trim() : ''
  if (rawUrl && isAbsoluteHttpUrl(rawUrl)) return absolutizeUrl(rawUrl)
  // Prefer webpack/static asset urls over filename+storage base (customer-supplied card, etc.)
  if (rawUrl && isDirectAssetUrl(rawUrl)) return rawUrl

  const rawFilename = image.filename != null ? String(image.filename).trim() : ''
  if (rawFilename) {
    if (isAbsoluteHttpUrl(rawFilename)) return absolutizeUrl(rawFilename)
    if (isDirectAssetUrl(rawFilename)) return rawFilename
    return image.pending
      ? base + 'pending/' + rawFilename
      : base + rawFilename
  }

  // Relative / non-http url with no filename — same fallback as product.vue getImages
  if (rawUrl) return base + rawUrl

  return null
}

/**
 * Resolve thumbnail URL for a catalog/task item.
 * Also accepts legacy `image_url` / `imageUrl` fields used by some material payloads.
 */
export function resolveItemImageUrl (item, imageBaseUrl, placeholder) {
  if (!item) return placeholder

  const base = imageBaseUrl || ''

  if (item.images != null) {
    if (Array.isArray(item.images)) {
      for (let i = 0; i < item.images.length; i++) {
        const resolved = resolveImage(item.images[i], base)
        if (resolved) return resolved
      }
    } else {
      const resolved = resolveImage(item.images, base)
      if (resolved) return resolved
    }
  }

  const legacy = item.image_url || item.imageUrl
  if (legacy) {
    const resolved = resolveImage(legacy, base)
    if (resolved) return resolved
  }

  return placeholder
}

/** True when the item has an image payload that resolves to a usable URL. */
export function itemHasResolvableImage (item, imageBaseUrl) {
  return !!resolveItemImageUrl(item, imageBaseUrl, null)
}

function isUsableImageEntry (entry) {
  if (!entry) return false
  if (typeof entry === 'string') return entry.trim() !== ''
  if (typeof entry !== 'object') return false
  return !!(entry.url || entry.filename)
}

/** Normalize API image payloads so UI always gets an array or null. */
export function normalizeItemImages (value) {
  if (value == null || value === '') return null
  if (Array.isArray(value)) {
    const cleaned = []
    for (let i = 0; i < value.length; i++) {
      const entry = value[i]
      if (typeof entry === 'string') {
        const nested = normalizeItemImages(entry)
        if (nested && nested.length) cleaned.push.apply(cleaned, nested)
        continue
      }
      if (isUsableImageEntry(entry)) cleaned.push(entry)
    }
    return cleaned.length ? cleaned : null
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed || trimmed === 'null' || trimmed === '[]') return null
    try {
      return normalizeItemImages(JSON.parse(trimmed))
    } catch (e) {
      if (isAbsoluteHttpUrl(trimmed)) {
        const url = absolutizeUrl(trimmed)
        return [{ url: url, filename: url.split('/').pop() || url }]
      }
      return [{ filename: trimmed }]
    }
  }
  if (typeof value === 'object') {
    if (value.url || value.filename) return [value]
    // mysql2 Buffer JSON shape that slipped through without parsing
    if (value.type === 'Buffer' && Array.isArray(value.data)) {
      try {
        const asString = Buffer.from(value.data).toString('utf8')
        return normalizeItemImages(asString)
      } catch (e) {
        return null
      }
    }
    return null
  }
  return null
}

export function withNormalizedImages (item) {
  if (!item || typeof item !== 'object') return item
  const images = normalizeItemImages(item.images)
  if (images === item.images) return item
  return Object.assign({}, item, { images })
}
